import React from "react";
import {
	Box,
	TextField,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Grid2 as Grid,
	Typography,
	useTheme,
	useMediaQuery,
	InputAdornment,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIcon } from "@mui/x-date-pickers/icons";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enAU } from "date-fns/locale";
import parseISO from "date-fns/parseISO";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusLabel from "../StatusLabel/StatusLabel";
import { addDays } from "date-fns";
import { useStyles } from "./Invoice.styles";
import { ManualInvoiceDetailsProps, ManualInvoiceEditableFields } from "./types";
import CurrencyTextField from "../CurrencyTextField/CurrencyTextField";
import { receiveStatus } from "../../util/receiveStatus";
import ReceiveComments from "../Comment/ReceiveComments";
import AsnButton from "../Asn/AsnButton";
import { DatePickerMobileActionBar } from "@/components/Common/DatePickerMobileActionBar";
import { getFreightCostHighWarning, isFreightCostHigh } from "@/util/freightUtil";
import { formatCurrency } from "@/util/currencyUtil";
import { AlertInformation } from "../AlertInformation/AlertInformation";

export const ManualInvoiceDetails = ({
	receive,
	storeName,
	editableInvoiceFields,
	handleInvoiceFieldChanged,
	showInvoiceDetails,
	onExpandClick,
	validateInvoiceNumber,
	asns,
	isModalAsnDetailsOpen,
	onAsnDetailsModalOpen,
	onAsnDetailsModalClose,
	captureFreightCostEnabled = false,
}: ManualInvoiceDetailsProps) => {
	const classes = useStyles();
	const handleChange =
		(field: keyof ManualInvoiceEditableFields) =>
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | Date | null) => {
			let value: string;
			if (event === null) {
				return; // Null date, don't update
			}
			if (event instanceof Date || (typeof event === 'object' && 'getTime' in event)) {
				const dateValue = event as Date;
				if (!dateValue || isNaN(dateValue.getTime())) {
					return; // Invalid date, don't update
				}
				value = dateValue.toISOString();
			} else {
				value = event?.target?.value ?? "";
			}

			if (field === "invoiceNumber") {
				value = value.toUpperCase()?.replace(/\s/g, "");
			}

			handleInvoiceFieldChanged && handleInvoiceFieldChanged(field, value);
		};

	const handleFreightChange = (_event: React.SyntheticEvent, value: number | string | null | undefined) => {
		const raw = value?.toString();
		handleInvoiceFieldChanged && handleInvoiceFieldChanged(
			"totalInvoicedFreightEx",
			raw == null || raw === "" ? undefined : raw
		);
	};

	const onInvoiceNumberTextFieldBlur = () => {
		validateInvoiceNumber(editableInvoiceFields?.invoiceNumber ?? "");
	};
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const freightInc = editableInvoiceFields?.totalInvoicedFreightInc ?? 0;
	// `receive` (enrichedReceive) is undefined until the first line item is scanned/entered.
	// In that case fall back to the freight so the total always includes freight charges.
	const totalCostInc = receive?.totalCostInc ?? freightInc;

	return (
		<React.Fragment>
			<Accordion
				defaultExpanded
				expanded={showInvoiceDetails}
				square
				onChange={() => onExpandClick({} as React.MouseEvent<HTMLButtonElement>)}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="invoiceheaderdetails-content"
					id="invoicerheaderdetails"
				>
					<Grid container alignItems="center" sx={{ width: "100%" }}>
						<Grid size="auto">
							<TextField
								id={`manualInvoiceDetails-${receive?.id ?? ""}`}
								label="Invoice No:"
								onChange={handleChange("invoiceNumber")}
								onClick={(event) => event.stopPropagation()}
								onFocus={(event) => event.stopPropagation()}
								value={editableInvoiceFields?.invoiceNumber ?? ""}
								autoComplete="off"
								size="small"
								variant="standard"
								slotProps={{ htmlInput: { className: classes.invoiceNumberInput } }}
								onBlur={onInvoiceNumberTextFieldBlur}
							/>
						</Grid>
						<Grid container justifyContent="flex-end" size="grow">
							<StatusLabel status={receive?.status || receiveStatus.open} size={isMobile ? "small" : "medium"} />
						</Grid>
					</Grid>
				</AccordionSummary>
				<AccordionDetails>
					<Grid container direction="column" size="grow">
						<Box display="flex" flexDirection="row">
							<Box>
								<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
									<DatePicker
										label="Invoice Date"
										value={editableInvoiceFields?.invoiceDate ? parseISO(editableInvoiceFields.invoiceDate) : null}
										onChange={handleChange("invoiceDate")}
										maxDate={addDays(new Date(), 6)}
										closeOnSelect
										slots={{ actionBar: DatePickerMobileActionBar }}
										slotProps={{
											textField: {
												id: "invoice-date",
												size: "small",
												variant: "standard",
												className: classes.dateInput,
											},
											field: { InputProps: { endAdornment: <InputAdornment position="end"><CalendarIcon fontSize="small" /></InputAdornment> } },
											actionBar: {
												actions: isMobile ? ["cancel", "accept"] : [],
												onSetDefaultDate: () => {
													if (!editableInvoiceFields?.invoiceDate) {
														handleInvoiceFieldChanged && handleInvoiceFieldChanged("invoiceDate", new Date().toISOString());
													}
												},
											} as any,
										}}
									/>
								</LocalizationProvider>
							</Box>
						</Box>
						<Box display="flex" flexDirection="row" style={{ marginTop: theme.spacing(1) }}>
							<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
								<DatePicker
									label="Delivered Date"
									onChange={handleChange("receivedDate")}
									value={editableInvoiceFields?.receivedDate ? parseISO(editableInvoiceFields.receivedDate) : null}
									maxDate={new Date()}
									closeOnSelect
									slots={{ actionBar: DatePickerMobileActionBar }}
									slotProps={{
										textField: {
											id: "delivered-date",
											size: "small",
											variant: "standard",
											className: classes.dateInput,
										},
										field: { InputProps: { endAdornment: <InputAdornment position="end"><CalendarIcon fontSize="small" /></InputAdornment> } },
										actionBar: {
											actions: isMobile ? ["cancel", "accept"] : [],
											onSetDefaultDate: () => {
												if (!editableInvoiceFields?.receivedDate) {
													handleInvoiceFieldChanged && handleInvoiceFieldChanged("receivedDate", new Date().toISOString());
												}
											},
										} as any,
									}}
								/>
							</LocalizationProvider>
						</Box>
						{captureFreightCostEnabled && (
							<>
								<Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: theme.spacing(2) }}>
									<CurrencyTextField
										id="freight-ex"
										label={
											<Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
												<LocalShippingIcon
													sx={{ color: theme.palette.custom.jbColours.darkOrange, fontSize: 30, marginRight: 1 }}
												/>
												<Typography component="span" sx={{ fontSize: "1.03rem" }}>
													Freight Cost Ex (optional)
												</Typography>
											</Box>
										}
										size="medium"
										variant="standard"
										value={editableInvoiceFields?.totalInvoicedFreightEx ?? ""}
										onChange={handleFreightChange}
										currencySymbol="$"
										minimumValue="0"
										emptyInputBehavior="null"
									/>
								</Box>
								{isFreightCostHigh(editableInvoiceFields?.totalInvoicedFreightEx, receive?.totalCostEx) && (
									<Box style={{ marginTop: theme.spacing(2) }}>
										<AlertInformation severity="warning" message={getFreightCostHighWarning(receive?.receiveType)} />
									</Box>
								)}
							</>
						)}
						<Box sx={{ marginTop: theme.spacing(3) }}>
							<Typography variant="body2" color="textSecondary" marginBottom={theme.spacing(0.5)}>
								Invoice Totals
							</Typography>
							<Typography variant="body2" marginBottom={theme.spacing(0.5)}>
								Line Total Cost Ex: {formatCurrency(receive?.totalCostEx)}
							</Typography>
							<Typography variant="body2" marginBottom={theme.spacing(0.5)}>
								Total Cost Inc: {formatCurrency(totalCostInc)}{" "}
								{captureFreightCostEnabled && (
									<Typography component="span" color="textSecondary" sx={{ fontSize: "0.77rem" }}>
										(incl freight charges)
									</Typography>
								)}
							</Typography>
						</Box>
						<Grid container sx={{ width: "100%" }} direction="row" justifyContent="space-between">
							<Grid size="auto">
								<Box display="flex" flexDirection="row" alignItems="flex-start" width="100%" style={{ marginTop: 4 }}>
									<Box display="flex" alignItems="center">
										<ReceiveComments initialComments={receive?.comments ?? []} receiveId={receive?.id} />
									</Box>
								</Box>
							</Grid>
							{asns?.length > 0 && (
								<Grid size="auto">
									<Box display="flex" flexDirection="row" alignItems="flex-start" width="100%" style={{ marginTop: 4 }}>
										<Box display="flex" alignItems="center">
											<AsnButton
												isOpen={isModalAsnDetailsOpen}
												asns={asns}
												fromLocationId={receive?.supplierId?.toString()}
												fromLocationName={receive?.supplierName}
												toLocationId={receive?.locationId?.toString()}
												toLocationName={storeName}
												onAsnDetailsModalOpen={onAsnDetailsModalOpen}
												onAsnDetailsModalClose={onAsnDetailsModalClose}
											/>
										</Box>
									</Box>
								</Grid>
							)}
						</Grid>
					</Grid>
				</AccordionDetails>
			</Accordion>
		</React.Fragment>
	);
};
